import { useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, MODAL_SIZES } from 'ui/modal';
import { DestructiveButton } from 'ui/button';
import { Checkbox } from 'ui/checkboxV2';
import { useModalContext } from 'utils/contexts/modal';
import { useDataSyncTranslations, useArchiveDestination } from 'modules/dataSync/hooks';
import { DataSyncModalState } from '../../contexts/ModalContext';
import {
  WarningIcon,
  WarningText,
  WarningSubtext,
  DescriptionText,
  DescriptionSubtext,
  StyledBanner,
  ErrorText,
} from './ArchiveDestinationModal.styles';
import { BannerVariants } from 'ui/banner/types';
import { FlexSpacer } from 'ui/flexSpacer';
import { ErrorFill16, WarningFill16 } from 'ui/icons';
import { colors } from 'ui/theme';

export const ArchiveDestinationModal = () => {
  const { modalState, hideModal } = useModalContext<DataSyncModalState>();
  const { t } = useDataSyncTranslations();
  const [isPermanentlyArchived, setIsPermanentlyArchived] = useState(false);

  const { archiveDestination, isLoading, isFailure } = useArchiveDestination({
    onCompleted: (data) => {
      if (data?.archiveDestination && 'destinationId' in data.archiveDestination) {
        // Success - close modal
        hideModal();
      }
    },
  });

  if (!modalState || modalState.action !== 'archiveDestination') {
    return null;
  }

  const { destinationId, destinationName } = modalState.payload || {};

  const handleArchive = () => {
    if (destinationId) {
      archiveDestination({
        variables: {
          destinationId,
        },
      });
    }
  };

  const handleCancel = () => {
    hideModal();
  };

  return (
    <Modal
      id="archive-destination-modal"
      isModalOpen={true}
      onClose={handleCancel}
      size={MODAL_SIZES.MEDIUM}
    >
      <ModalHeader>
        <FlexSpacer gap="half" alignItems="center">
          <ErrorFill16 color={colors.iconDestructive} />
          <span>{t('modal.archiveDestination.title', { destination: destinationName })}</span>
        </FlexSpacer>
      </ModalHeader>
      <ModalBody>
        <FlexSpacer flexDirection="column" alignItems="flex-start" gap="base">
          {isFailure && (
            <StyledBanner variant={BannerVariants.ERROR}>
              <FlexSpacer gap="threeQuarter" alignItems="center">
                <ErrorFill16 color={colors.iconDestructive} />
                <ErrorText>{t('modal.archiveDestination.errorMessage')}</ErrorText>
              </FlexSpacer>
            </StyledBanner>
          )}

          <StyledBanner variant={BannerVariants.WARNING}>
            <FlexSpacer gap="threeQuarter" alignItems="flex-start">
              <WarningIcon>
                <WarningFill16 color={colors.warning100} />
              </WarningIcon>
              <FlexSpacer flexDirection="column" alignItems="flex-start" gap="quarter">
                <WarningText>{t('modal.archiveDestination.warning')}</WarningText>
                <WarningSubtext>{t('modal.archiveDestination.warningSubtext')}</WarningSubtext>
              </FlexSpacer>
            </FlexSpacer>
          </StyledBanner>

          <FlexSpacer flexDirection="column" alignItems="flex-start" gap="noGap">
            <DescriptionText>{t('modal.archiveDestination.description')}</DescriptionText>
            <DescriptionSubtext>
              {t('modal.archiveDestination.descriptionSubtext')}
            </DescriptionSubtext>
          </FlexSpacer>

          <Checkbox
            id="permanently-archive"
            status={isPermanentlyArchived}
            setChecked={(checked) => setIsPermanentlyArchived(checked === true)}
            label={t('modal.archiveDestination.permanentlyArchive')}
          />
        </FlexSpacer>
      </ModalBody>
      <ModalFooter>
        <FlexSpacer gap="base" justifyContent="flex-end">
          <DestructiveButton
            onClick={handleArchive}
            isDisabled={!isPermanentlyArchived}
            isLoading={isLoading}
            data-test="archive-destination-button"
          >
            {t('modal.archiveDestination.archiveButton')}
          </DestructiveButton>
        </FlexSpacer>
      </ModalFooter>
    </Modal>
  );
};
